// AnnualLeaveSingleTableMobile.tsx
import "./AnnualLeaveSingleTableMobile.scss";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Badge, Button, Collapse, Popover, Tooltip, theme } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { months } from "../../utils/constants";
import type {
  AnnualPlanItem,
  BankHolidays,
  LeaveRequest,
  User,
} from "../../utils/types";

type Props = {
  annualLeaveYear: string;
  leaveRequests: LeaveRequest[];
  user: User | null;
  onDateClick: (user: User | null, date: Date) => void;
  selectedLeaveTypes: string[];
  bankHolidays: BankHolidays[];
  annualPlan: AnnualPlanItem[];
};

const weekday2 = (d: Date) =>
  d.toLocaleDateString("en-GB", { weekday: "short" }).slice(0, 2); // Mo, Tu...

// Maps every request status to the legend toggle key that shows/hides it -
// mirrors CALENDAR_VISIBLE_STATUSES in MyCalendar.tsx and the desktop
// AnnualLeaveSingleTable's STATUS_BUCKET.
const STATUS_BUCKET: Record<string, string> = {
  AWAITING_MANAGER: "PENDING",
  AWAITING_ADMINISTRATIV: "PENDING",
  RETURNED_FOR_CORRECTION: "PENDING",
  AWAITING_DOCUMENT_REVIEW: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
  CANCELLATION_REJECTED: "CANCELLED",
  AWAITING_MANAGER_CANCELLATION: "PENDING_CANCELLED",
  AWAITING_ADMINISTRATIV_CANCELLATION: "PENDING_CANCELLED",
};

const STATUS_CLASS: Record<string, string> = {
  PENDING: "leave-awaiting-manager",
  APPROVED: "leave-approved",
  REJECTED: "leave-rejected",
  CANCELLED: "leave-cancelled",
  PENDING_CANCELLED: "leave-pending-cancelled",
};

const STATUS_TAG: Record<string, { label: string; className: string }> = {
  PENDING: { label: "Pending", className: "pending" },
  APPROVED: { label: "Approved", className: "approved" },
  REJECTED: { label: "Rejected", className: "rejected" },
  CANCELLED: { label: "Cancelled", className: "cancelled" },
  PENDING_CANCELLED: { label: "Pending Cancelled", className: "pending-cancelled" },
};

const AnnualLeaveSingleMobile = ({
  annualLeaveYear,
  user,
  leaveRequests,
  onDateClick,
  selectedLeaveTypes,
  bankHolidays,
  annualPlan,
}: Props) => {
  const { token } = theme.useToken();

  const year = useMemo(() => parseInt(annualLeaveYear, 10), [annualLeaveYear]);
  const monthsList = useMemo(() => months(year), [year]);
  const currentMonthIndex = new Date().getMonth();
  const monthRefs = useRef<Array<HTMLDivElement | null>>([]);

  const leaveDayInfoMap = useMemo(() => {
    const map = new Map<
      string,
      { type: string; comment: string; substituteName: string; status: string }
    >();
    leaveRequests.forEach((lq) => {
      lq.leaveDays.forEach((day) => {
        map.set(day, {
          type: lq.requestType,
          comment: lq.comment ?? "",
          substituteName: lq.substituteName ?? "",
          status: lq.requestStatus,
        });
      });
    });
    return map;
  }, [leaveRequests]);

  const bankHolidayMap = useMemo(() => {
    return new Map(
      bankHolidays.map((h) => [
        new Date(h.date).toLocaleDateString("sv-SE"),
        h.description,
      ]),
    );
  }, [bankHolidays]);

  const annualPlanDates = useMemo(() => {
    const datesSet = new Set<string>();
    annualPlan.forEach(({ startDate, endDate }) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        datesSet.add(d.toISOString().split("T")[0]);
      }
    });
    return datesSet;
  }, [annualPlan]);

  const getCellMeta = useCallback(
    (date: Date, formattedDate: string) => {
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      const leaveInfo = leaveDayInfoMap.get(formattedDate);
      const leaveType = leaveInfo?.type;
      const status = leaveInfo?.status;
      const statusBucket = status ? STATUS_BUCKET[status] : undefined;

      const bankHolidayDescription = bankHolidayMap.get(formattedDate);
      const isBankHoliday =
        !!bankHolidayDescription && selectedLeaveTypes.includes("BANK_HOLIDAY");

      const isAnnualPlanDay =
        annualPlanDates.has(formattedDate) &&
        selectedLeaveTypes.includes("ANNUAL_PLAN");

      // do not hide annual plan days even if the status filter is off
      if (
        !isAnnualPlanDay &&
        statusBucket &&
        !selectedLeaveTypes.includes(statusBucket)
      ) {
        return {
          hidden: true,
          className: "day hidden-cell",
          popup: "",
          onClick: undefined as undefined,
          hasRequest: true,
          isAnnualPlanDay: false,
          statusBucket: undefined,
        };
      }

      const isLeaveDay = !!leaveType && selectedLeaveTypes.includes(leaveType);
      const hasRequest = !!leaveType;

      // popup: explicit overlap
      let popup = "";
      if (isAnnualPlanDay && hasRequest) {
        popup = `Annual Plan Day + ${status || "request"}: ${
          leaveInfo?.type
        } - ${leaveInfo?.comment || "No comment"} | Substitute: ${
          leaveInfo?.substituteName || "N/A"
        } | Status: ${status}`;
      } else if (isAnnualPlanDay) {
        popup = "Annual Plan Day";
      } else if (isBankHoliday) {
        popup = `Holiday: ${bankHolidayDescription}`;
      } else if (hasRequest) {
        popup = `${leaveInfo?.type} - ${
          leaveInfo?.comment || "No comment"
        } | Substitute: ${leaveInfo?.substituteName || "N/A"} | Status: ${status}`;
      } else if (isWeekend) {
        popup = "Weekend";
      }

      const statusClass = statusBucket ? STATUS_CLASS[statusBucket] : "";

      const overlapClass =
        isAnnualPlanDay && hasRequest ? "annual-plan-overlap" : "";

      const className = [
        "day",
        isBankHoliday ? "BANK_HOLIDAY" : "",
        isWeekend ? "weekend" : "",
        statusClass,
        isLeaveDay ? `leave-${leaveType}` : "",
        isAnnualPlanDay ? "annual-plan-day" : "",
        overlapClass,
      ]
        .filter(Boolean)
        .join(" ");

      const allowClick =
        !isWeekend && !isLeaveDay && !isBankHoliday && !isAnnualPlanDay;

      return {
        hidden: false,
        className,
        popup,
        onClick: allowClick ? () => onDateClick(user, date) : undefined,
        hasRequest,
        isAnnualPlanDay,
        statusBucket,
      };
    },
    [
      annualPlanDates,
      bankHolidayMap,
      leaveDayInfoMap,
      onDateClick,
      selectedLeaveTypes,
      user,
    ],
  );

  const employeeName = leaveRequests[0]?.employeeName || "";
  const daysRemaining = leaveRequests[0]?.currentYearRemainingDays ?? "";

  const infoBtnMsg =
    "Calculation:\n(Current year days + Previous year days) - Used current year days - Used previous year days";

  useEffect(() => {
    const el = monthRefs.current[currentMonthIndex];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentMonthIndex, annualLeaveYear]);

  return (
    <div
      className="al-mobile"
      style={
        {
          // theme-aware CSS vars (AntD light/dark)
          "--al-bg": token.colorBgContainer,
          "--al-bg-subtle": token.colorFillTertiary,
          "--al-border": token.colorBorderSecondary,
          "--al-text": token.colorText,
          "--al-text-secondary": token.colorTextSecondary,

          // status colors from AntD tokens
          "--al-approved": token.colorSuccess,
          "--al-pending": token.colorInfo,
          "--al-rejected": token.colorError,
          "--al-cancelled": token.colorTextTertiary,
          "--al-pending-cancelled": token.colorWarningHover,
          "--al-annual": token.colorWarning,
          "--al-annual-strong": token.colorWarningHover,
          "--al-holiday": token.colorError,
        } as React.CSSProperties & Record<`--${string}`, string>
      }
    >
      <div className="header">
        <div className="name">{employeeName || "—"}</div>

        <div className="remaining">
          <span className="label">Days remaining</span>
          <span className="value">{daysRemaining || "—"}</span>
          <Tooltip title={<span className="info-tooltip">{infoBtnMsg}</span>}>
            <Button
              className="info-btn"
              type="text"
              icon={<QuestionCircleOutlined />}
            />
          </Tooltip>
        </div>
      </div>

      <Collapse
        className="months"
        defaultActiveKey={[String(currentMonthIndex)]}
        items={monthsList.map((m, monthIndex) => {
          const monthTitle = (
            <div className="month-title">
              <span>{m.name}</span>
              <span className="range">1-{m.days}</span>
            </div>
          );

          return {
            key: String(monthIndex),
            label: monthTitle,
            children: (
              <div
                ref={(el) => {
                  monthRefs.current[monthIndex] = el;
                }}
                className="month-body"
              >
                <div
                  className="days-strip"
                  role="grid"
                  aria-label={`${m.name} ${annualLeaveYear}`}
                >
                  {Array.from({ length: m.days }, (_, dayIndex) => {
                    const date = new Date(year, monthIndex, dayIndex + 1);
                    const formattedDate = date.toLocaleDateString("sv-SE");
                    const meta = getCellMeta(date, formattedDate);

                    if (meta.hidden) {
                      return (
                        <div
                          key={dayIndex}
                          className="day hidden-cell"
                          aria-hidden="true"
                        />
                      );
                    }

                    const content = meta.popup ? (
                      <div className="popover-content">
                        <div className="popover-tags">
                          {meta.isAnnualPlanDay && (
                            <span className="tag annual">Annual plan</span>
                          )}
                          {meta.hasRequest && meta.statusBucket && (
                            <span
                              className={`tag ${STATUS_TAG[meta.statusBucket].className}`}
                            >
                              {STATUS_TAG[meta.statusBucket].label}
                            </span>
                          )}
                        </div>
                        <div>{meta.popup}</div>
                      </div>
                    ) : null;

                    const dayNode = (
                      <button
                        type="button"
                        key={dayIndex}
                        className={meta.className}
                        onClick={meta.onClick}
                        disabled={!meta.onClick}
                        aria-label={`${dayIndex + 1} ${m.name} ${annualLeaveYear}`}
                      >
                        <span className="day-num">{dayIndex + 1}</span>
                        <span className="day-dow" aria-hidden="true">
                          {weekday2(date)}
                        </span>
                      </button>
                    );

                    return meta.popup ? (
                      <Popover
                        key={dayIndex}
                        content={content}
                        trigger="click"
                        placement="top"
                      >
                        {dayNode}
                      </Popover>
                    ) : (
                      dayNode
                    );
                  })}
                </div>

                <div className="legend">
                  <Badge status="success" text="Approved" />
                  <Badge status="processing" text="Pending" />
                  <Badge status="warning" text="Annual plan" />
                  <Badge status="default" text="Weekend / blocked" />
                </div>
              </div>
            ),
          };
        })}
      />
    </div>
  );
};

export default AnnualLeaveSingleMobile;
